import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '../../../environments/environment';
import { appVersion } from '../../../version';
import { identityPaths, layout } from '../../app.constants';
import { CheckinsAccessService } from '../auth/checkins-access.service';
import { CheckinsRole } from '../auth/checkins-roles';
import { profilePictureToShow } from '../auth/profile-picture';
import { SESSION_PROVIDER } from '../auth/session-provider';
import { RockApp, RockAppsService } from '../services/rock-apps.service';

/** The seeded RockApp key for this app (distinct from the OIDC clientId 'rockapps-checkins'). */
const CURRENT_APP_KEY = 'checkins';

interface NavItem {
  displayName: string;
  icon: string;
  route: string;
  exact?: boolean;
  /** Minimum Checkins role to see this item; omit for any authenticated user. */
  requires?: CheckinsRole;
}

/**
 * The app shell: FDC-style dark-azure toolbar (hamburger, brand, Rock Apps switcher, user menu)
 * over a collapsible left navigation rail and the routed content area. Authenticated pages render
 * inside this shell; the auth callback renders outside it.
 */
@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  animations: [
    trigger('menuIconAnimation', [
      state('menu', style({ transform: 'rotate(0deg) scale(1)' })),
      state('close', style({ transform: 'rotate(180deg) scale(1.2)' })),
      transition('menu <=> close', animate('300ms ease-in-out')),
    ]),
  ],
  template: `
    <mat-toolbar class="shell__header">
      <button mat-icon-button (click)="toggle()" aria-label="Toggle navigation">
        <mat-icon [@menuIconAnimation]="navExpanded() ? 'close' : 'menu'">{{ navExpanded() ? 'close' : 'menu' }}</mat-icon>
      </button>
      <nav class="shell__breadcrumb" aria-label="App location">
        <a class="shell__brand" routerLink="/" aria-label="Rock Apps home">
          <img src="assets/images/globe_white.png" class="shell__logo" alt="" />
          @if (!isCompact()) { <span class="shell__brand-name">Rock Apps</span> }
        </a>

        <span class="shell__crumb-sep" aria-hidden="true">/</span>

        @if (otherApps().length > 0) {
          <button type="button" class="shell__app" [matMenuTriggerFor]="appsMenu" aria-label="Switch app">
            Check-ins <mat-icon class="shell__app-caret">arrow_drop_down</mat-icon>
          </button>
          <mat-menu #appsMenu="matMenu" class="apps-menu">
            @for (app of otherApps(); track app.id) {
              <button mat-menu-item (click)="launch(app)">
                <span>{{ app.name }}</span>
              </button>
            }
          </mat-menu>
        } @else {
          <span class="shell__app shell__app--static">Check-ins</span>
        }
      </nav>

      <span class="shell__spacer"></span>

      <a class="shell__profile" [href]="profileUrl" target="_blank" rel="noopener"
        aria-label="My Profile" [matTooltip]="isCompact() ? 'My Profile' : ''">
        @if (profilePicture(); as picture) {
          <!-- Hidden from assistive tech: the link is already named "My Profile". -->
          <img class="shell__avatar" [src]="picture" alt="" aria-hidden="true"
            (error)="failedPicture.set(picture)" />
        } @else {
          <mat-icon>account_circle</mat-icon>
        }
        @if (!isCompact()) { <span>My Profile</span> }
      </a>
    </mat-toolbar>

    <mat-sidenav-container class="shell__body">
      <mat-sidenav class="shell__nav"
        [mode]="isMobile() ? 'over' : 'side'"
        [opened]="isMobile() ? opened() : true"
        [class.shell__nav--collapsed]="railCollapsed()"
        (openedChange)="onOpenedChange($event)"
        [autoFocus]="false">
        <div class="shell__nav-inner">
          <mat-nav-list>
            @for (item of navItems(); track item.route) {
              <a mat-list-item [routerLink]="item.route" routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: !!item.exact }"
                [matTooltip]="railCollapsed() ? item.displayName : ''" matTooltipPosition="right">
                <mat-icon matListItemIcon>{{ item.icon }}</mat-icon>
                @if (!railCollapsed()) { <span matListItemTitle>{{ item.displayName }}</span> }
              </a>
            }
          </mat-nav-list>

          <mat-nav-list class="shell__nav-footer">
            <button mat-list-item type="button" class="shell__logout" (click)="logout()"
              [matTooltip]="railCollapsed() ? 'Sign out' : ''" matTooltipPosition="right">
              <mat-icon matListItemIcon>logout</mat-icon>
              @if (!railCollapsed()) { <span matListItemTitle>Sign out</span> }
            </button>
          </mat-nav-list>

          @if (!railCollapsed()) {
            <div class="shell__version">v{{ version }}</div>
          }
        </div>
      </mat-sidenav>

      <mat-sidenav-content class="shell__content">
        <router-outlet></router-outlet>
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  styles: [`
    :host { display: block; }
    .shell__header {
      position: sticky;
      top: 0;
      z-index: 10;
      gap: 0.25rem;
      /* Breathing room between the switcher and the window edge (Material's default is 16px). */
      padding-right: 1.5rem;
      color: #fff;
      box-shadow: var(--elev-2);
    }
    .shell__header .mat-icon, .shell__header button { color: #fff; }
    /* GitHub-style location breadcrumb: [logo Rock Apps] / [Check-ins ▾]. Left = where you are. */
    .shell__breadcrumb {
      display: flex;
      align-items: center;
      gap: 0.15rem;
      margin-left: 0.5rem;
      min-width: 0;
    }
    .shell__brand {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      padding: 0.3rem 0.5rem;
      border-radius: 0.5rem;
      color: #fff;
      text-decoration: none;
      transition: background 0.15s ease;
    }
    .shell__brand:hover { background: rgba(255, 255, 255, 0.14); }
    .shell__logo { height: 24px; width: auto; display: block; }
    .shell__brand-name {
      font-size: 1.05rem;
      font-weight: 500;
      letter-spacing: 0.005em;
      white-space: nowrap;
    }
    /* The "/" divider between suite and app — deliberately faint, like GitHub's org/repo slash. */
    .shell__crumb-sep {
      color: rgba(255, 255, 255, 0.55);
      font-size: 1.2rem;
      font-weight: 300;
    }
    .shell__spacer { flex: 1 1 auto; }

    .shell__app {
      display: inline-flex;
      align-items: center;
      gap: 0.1rem;
      padding: 0.3rem 0.6rem;
      border: none;
      border-radius: 0.5rem;
      background: transparent;
      color: #fff;
      font-family: inherit;
      font-weight: 600;
      font-size: 1.05rem;
      text-decoration: none;
      white-space: nowrap;
      cursor: pointer;
      transition: background 0.15s ease;
    }
    .shell__app:hover { background: rgba(255, 255, 255, 0.14); }
    /* No switch targets: show the current app as plain text, no button affordance. */
    .shell__app--static { cursor: default; }
    .shell__app--static:hover { background: transparent; }
    .shell__app-caret { color: #fff; }

    /* Deep link out to Identity's account page. Deliberately lighter than .shell__app: this is a
       utility link, while the switcher anchors the right side as the app's identity. */
    .shell__profile {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      padding: 0.3rem 0.6rem;
      border-radius: 0.5rem;
      color: #fff;
      font-weight: 500;
      font-size: 0.95rem;
      text-decoration: none;
      white-space: nowrap;
      transition: background 0.15s ease;
    }
    .shell__profile mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }
    /* The person's own photograph in place of the icon: a touch larger so a face reads at toolbar
       size, cropped to a circle rather than squashed, with a hairline ring so a light photo does not
       bleed into the azure bar. */
    .shell__avatar {
      display: block;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
      box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.4);
    }
    .shell__profile:hover { background: rgba(255, 255, 255, 0.14); }

    .shell__body { height: calc(100vh - 64px); }
    .shell__nav {
      width: 220px;
      border-right: 1px solid var(--color-neutral-300);
      background: var(--color-neutral-200);
      overflow-x: hidden;
      transition: width 0.2s ease;
      padding-top: 0.5rem;
    }
    /* Clip the Material drawer's inner scroll container so the collapsed rail shows no scrollbar. */
    .shell__nav ::ng-deep .mat-drawer-inner-container { overflow-x: hidden; display: flex; }
    .shell__nav-inner { display: flex; flex-direction: column; flex: 1 1 auto; }
    /* Pin the logout list to the bottom of the rail. */
    .shell__nav-footer { margin-top: auto; border-top: 1px solid var(--color-neutral-300); }
    .shell__logout { cursor: pointer; }
    /* Build stamp under the sign-out row — only rendered while the rail shows labels. */
    .shell__version {
      padding: 0.5rem 1rem 0.75rem;
      color: var(--color-neutral-600);
      font-size: 0.75rem;
      letter-spacing: 0.02em;
      white-space: nowrap;
    }
    .shell__nav--collapsed { width: 64px; }
    .shell__nav a {
      border-radius: var(--radius-lg, 0.6rem);
      margin: 0.15rem 0.5rem;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .shell__nav a:hover { background: var(--color-primary-200); }
    .shell__nav a.active {
      background: var(--color-primary-300);
      font-weight: 600;
      color: var(--color-primary-900);
    }
    .shell__nav a.active mat-icon { color: var(--color-primary-700); }
    .shell__content { background: var(--color-neutral-100); }


    @media (max-width: 40rem) {
      .shell__body { height: calc(100vh - 56px); }
    }
  `],
})
export class ShellComponent {
  private readonly session = inject(SESSION_PROVIDER);
  private readonly rockApps = inject(RockAppsService);
  private readonly access = inject(CheckinsAccessService);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Phone widths: the rail becomes a dismissible overlay drawer instead of a persistent rail. */
  readonly isMobile = signal(false);
  /** Narrow toolbar: drop text labels (brand name, profile) so the app switcher keeps its room. */
  readonly isCompact = signal(false);
  /** Desktop only — the rail is narrowed to icons. On mobile the drawer is always full width. */
  readonly collapsed = signal(true);
  /** Mobile only — the overlay drawer is open. */
  readonly opened = signal(false);

  readonly apps = signal<RockApp[]>([]);
  readonly appsError = signal(false);

  /** Icons-only rail: desktop-collapsed. Drives labels, tooltips and the narrow width. */
  readonly railCollapsed = computed(() => !this.isMobile() && this.collapsed());
  /** Whether the nav currently reads as open — drives the hamburger's menu/close icon. */
  readonly navExpanded = computed(() => (this.isMobile() ? this.opened() : !this.collapsed()));

  /** The user's other Rock Apps (everything except this one) — the switch targets. */
  readonly otherApps = computed(() => this.apps().filter(app => app.key !== CURRENT_APP_KEY));

  /** Identity's "My account" page — Identity owns profile for the whole suite, so we link out. */
  readonly profileUrl = `${environment.auth.authority}${identityPaths.account}`;

  /** The picture that last failed to load, so the toolbar shows the icon instead of a broken image. */
  readonly failedPicture = signal<string | null>(null);

  /** The signed-in person's own photograph for "My Profile", or null to show the generic icon. */
  readonly profilePicture = computed(() =>
    profilePictureToShow(this.session.picture(), this.failedPicture()));

  /** Unified product version, stamped by `npm run release`. Shown at the foot of the open rail. */
  readonly version = appVersion;

  constructor() {
    // Load up front so the header knows whether to show the switcher dropdown or a plain home link.
    this.loadApps();

    this.breakpoints.observe([layout.mobileBreakpoint, layout.compactBreakpoint])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const mobile = this.breakpoints.isMatched(layout.mobileBreakpoint);
        this.isMobile.set(mobile);
        this.isCompact.set(this.breakpoints.isMatched(layout.compactBreakpoint));
        if (mobile) {
          this.opened.set(false); // Never strand the overlay open when resizing down.
        }
      });

    // After navigating, dismiss the nav so it never sits over the destination page: close the
    // overlay on mobile, collapse the rail on desktop. Mirrors the Food Distribution client.
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isMobile()) {
          this.opened.set(false);
        } else {
          this.collapsed.set(true);
        }
      });
  }

  private readonly allNavItems: NavItem[] = [
    { displayName: 'Home', icon: 'home', route: '/', exact: true },
    { displayName: 'Events', icon: 'event', route: '/events', requires: 'User' },
    { displayName: 'Register', icon: 'how_to_reg', route: '/register', requires: 'User' },
    { displayName: 'Check in', icon: 'fact_check', route: '/checkin', requires: 'User' },
    { displayName: 'Reports', icon: 'insights', route: '/reports', requires: 'Admin' },
    { displayName: 'Duplicates', icon: 'people_alt', route: '/duplicates', requires: 'Admin' },
    { displayName: 'Tools', icon: 'build', route: '/tools', requires: 'SuperAdmin' },
  ];

  /** The rail, filtered to what the current user's role can reach. */
  readonly navItems = computed(() =>
    this.allNavItems.filter(item => item.requires == null || this.access.satisfies(item.requires)));

  /** The hamburger drives whichever axis applies: open/close on mobile, collapse/expand on desktop. */
  toggle(): void {
    if (this.isMobile()) {
      this.opened.update(value => !value);
    } else {
      this.collapsed.update(value => !value);
    }
  }

  /** Keeps our state in sync when Material closes the drawer itself (backdrop click, Escape). */
  onOpenedChange(isOpen: boolean): void {
    if (this.isMobile()) {
      this.opened.set(isOpen);
    }
  }

  loadApps(): void {
    this.appsError.set(false);
    this.rockApps.myApps().subscribe({
      next: apps => this.apps.set(apps),
      error: () => this.appsError.set(true),
    });
  }

  launch(app: RockApp): void {
    window.location.assign(app.launchUrl);
  }

  /**
   * Signs out, after confirming.
   *
   * Signing out of Identity revokes every token for this account across every device, not just
   * this browser — `ConnectController.Logout` walks `FindBySubjectAsync` and revokes the lot. On a
   * shared station account that means any volunteer reaching for this button ends registration on
   * whichever other machines share it, and the client re-launches the OIDC redirect on the
   * resulting 401, so they are not merely errored — they land on a login screen mid-registration.
   *
   * Native confirm() rather than a dialog component, matching how event deletion and registration
   * cancellation already confirm. It is keyboard-operable and Escape-dismissible for free.
   */
  logout(): void {
    const message = 'Sign out? Signing back in needs this account’s password, '
      + 'and any other computer signed in with it will be signed out too.';

    if (!confirm(message)) {
      return;
    }

    this.session.logout();
  }
}
